import { inject, Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext } from '@ngxs/store';
import { catchError, forkJoin, Observable, of, tap } from 'rxjs';
import { BusinessServices, ITeam } from '@shs-digi-care-monorepo/interfaces';
import { TeamActions } from './team.actions';
import { TeamService } from '../../services';

export interface TeamStateModel {
    teams: ITeam[];
    loading: boolean;
    loaded: boolean;
    error: string | null;
}

const defaults: TeamStateModel = {
    teams: [],
    loading: false,
    loaded: false,
    error: null
};

@State<TeamStateModel>({
    name: 'teamState',
    defaults
})
@Injectable()
export class TeamState {
    private readonly teamService = inject(TeamService);

    // ============================================
    // SELECTORS
    // ============================================

    /**
     * Returns all teams from the store.
     */
    @Selector()
    public static allTeams(state: TeamStateModel): ITeam[] {
        return state.teams;
    }

    /**
     * Returns whether the teams are currently loading.
     */
    @Selector()
    public static loading(state: TeamStateModel): boolean {
        return state.loading;
    }

    /**
     * Returns whether the teams have been loaded.
     */
    @Selector()
    public static loaded(state: TeamStateModel): boolean {
        return state.loaded;
    }

    /**
     * Returns any error that occurred during loading.
     */
    @Selector()
    public static error(state: TeamStateModel): string | null {
        return state.error;
    }

    /**
     * Creates a selector for teams filtered by a specific business service/stream.
     * @param stream The business service to filter by
     */
    public static teamsByStream(stream: BusinessServices): (state: TeamStateModel) => ITeam[] {
        return createSelector([TeamState], (state: TeamStateModel) =>
            state.teams.filter((team) => team.businessServices?.includes(stream) ?? false)
        );
    }

    /**
     * Selector for OM EMEA teams.
     */
    @Selector()
    public static emeaTeams(state: TeamStateModel): ITeam[] {
        return state.teams.filter((team) => team.businessServices?.includes(BusinessServices.OM_EMEA) ?? false);
    }

    /**
     * Selector for OM NAM teams.
     */
    @Selector()
    public static namTeams(state: TeamStateModel): ITeam[] {
        return state.teams.filter((team) => team.businessServices?.includes(BusinessServices.OM_NAM) ?? false);
    }

    /**
     * Selector for CLW teams.
     */
    @Selector()
    public static clwTeams(state: TeamStateModel): ITeam[] {
        return state.teams.filter((team) => team.businessServices?.includes(BusinessServices.CLW) ?? false);
    }

    /**
     * Returns only external teams (non-internal).
     */
    @Selector()
    public static externalTeams(state: TeamStateModel): ITeam[] {
        return state.teams.filter((team) => !team.internal);
    }

    /**
     * Returns only internal teams.
     */
    @Selector()
    public static internalTeams(state: TeamStateModel): ITeam[] {
        return state.teams.filter((team) => team.internal === true);
    }

    // ============================================
    // ACTIONS
    // ============================================

    /**
     * Loads all teams for all business services in parallel.
     */
    @Action(TeamActions.LoadAll)
    public loadAll(ctx: StateContext<TeamStateModel>): Observable<unknown> {
        const state = ctx.getState();

        // Skip if already loaded or currently loading
        if (state.loaded || state.loading) {
            return of(null);
        }

        ctx.patchState({ loading: true, error: null });

        // Load teams for all business services in parallel
        const streams = Object.values(BusinessServices);
        const requests = streams.map((stream) =>
            this.teamService.getTeams(0, 500, stream).pipe(
                catchError((error) => {
                    console.error(`Error loading teams for ${stream}:`, error);
                    return of({ items: [] });
                })
            )
        );

        return forkJoin(requests).pipe(
            tap((responses) => {
                // Combine all teams and remove duplicates by ID
                const allTeams: ITeam[] = [];
                const seenIds = new Set<string>();

                responses.forEach((response) => {
                    const items = (response as { items: ITeam[] }).items || [];
                    items.forEach((team) => {
                        if (!seenIds.has(team.id)) {
                            seenIds.add(team.id);
                            allTeams.push(team);
                        }
                    });
                });

                ctx.patchState({
                    teams: allTeams,
                    loading: false,
                    loaded: true
                });
            }),
            catchError((error) => {
                ctx.patchState({
                    loading: false,
                    error: error.message || 'Failed to load teams'
                });
                return of(null);
            })
        );
    }

    /**
     * Loads teams for a specific business service and appends them to the store.
     */
    @Action(TeamActions.LoadByStream)
    public loadByStream(
        ctx: StateContext<TeamStateModel>,
        { businessService }: TeamActions.LoadByStream
    ): Observable<unknown> {
        ctx.patchState({ loading: true, error: null });

        return this.teamService.getTeams(0, 500, businessService).pipe(
            tap((response) => {
                const state = ctx.getState();
                const existingIds = new Set(state.teams.map((t) => t.id));
                const newTeams = response.items.filter((team: ITeam) => !existingIds.has(team.id));

                ctx.patchState({
                    teams: [...state.teams, ...newTeams],
                    loading: false
                });
            }),
            catchError((error) => {
                ctx.patchState({
                    loading: false,
                    error: error.message || `Failed to load teams for ${businessService}`
                });
                return of(null);
            })
        );
    }

    /**
     * Sets the teams directly (replaces all teams).
     */
    @Action(TeamActions.SetTeams)
    public setTeams(ctx: StateContext<TeamStateModel>, { teams }: TeamActions.SetTeams): void {
        ctx.patchState({
            teams,
            loaded: true
        });
    }

    /**
     * Appends teams to the existing list without duplicates.
     */
    @Action(TeamActions.AppendTeams)
    public appendTeams(ctx: StateContext<TeamStateModel>, { teams }: TeamActions.AppendTeams): void {
        const state = ctx.getState();
        const existingIds = new Set(state.teams.map((t) => t.id));
        const newTeams = teams.filter((team) => !existingIds.has(team.id));

        ctx.patchState({
            teams: [...state.teams, ...newTeams]
        });
    }

    /**
     * Updates a team via API and patches it in the store.
     */
    @Action(TeamActions.Update)
    public update(ctx: StateContext<TeamStateModel>, { id, payload }: TeamActions.Update): Observable<unknown> {
        return this.teamService.updateTeam(id, payload).pipe(
            tap((updatedTeam) => {
                const state = ctx.getState();
                ctx.patchState({
                    teams: state.teams.map((t) => (t.id === id ? { ...t, ...updatedTeam } : t))
                });
            }),
            catchError((error) => {
                ctx.patchState({ error: error.message || 'Failed to update team' });
                return of(null);
            })
        );
    }

    /**
     * Clears all teams from the store.
     */
    @Action(TeamActions.Clear)
    public clear(ctx: StateContext<TeamStateModel>): void {
        ctx.patchState({
            teams: [],
            loaded: false,
            error: null
        });
    }
}
